// frontend/src/services/credit/creditService.js
import api from '../api';
import errorService from '../error/errorService';

/**
 * Credit Service - Handles all credit-related operations
 */
class CreditService {
  constructor() {
    this.creditBalance = null;
    this.isRestricted = false;
    this.listeners = [];
  }

  /**
   * Get the current credit balance
   * @param {boolean} forceRefresh - Whether to force a refresh from the server
   * @returns {Promise<number>} - Promise that resolves with the credit balance
   */
  async getCreditBalance(forceRefresh = false) {
    try {
      // Return cached balance if available and not forcing refresh
      if (this.creditBalance !== null && !forceRefresh) {
        return this.creditBalance;
      }
      
      // Get credit balance from API
      const { data } = await api.get('/credit-balance');
      
      // Update stored balance
      this.creditBalance = data.balance;
      
      // Notify listeners
      this._notifyListeners();
      
      return data.balance;
    } catch (error) {
      console.error("Error fetching credit balance:", error);
      // Don't update stored balance on error
      return this.creditBalance || 0;
    }
  }

  /**
   * Check if the user is restricted from translation
   * @param {boolean} forceRefresh - Whether to force a refresh from the server
   * @returns {Promise<boolean>} - Promise that resolves with restriction status
   */
  async checkRestrictionStatus(forceRefresh = false) {
    try {
      // Return cached status if available and not forcing refresh
      if (this.isRestricted !== null && !forceRefresh) {
        return this.isRestricted;
      }
      
      // Get restriction status from API
      const { data } = await api.get('/translation-restriction');
      
      // Update stored status
      this.isRestricted = data.restricted;
      
      return data.restricted;
    } catch (error) {
      console.error("Error checking translation restriction:", error);
      // Don't update stored status on error
      return this.isRestricted || false;
    }
  }

  /**
   * Update credit balance from the server
   * @returns {Promise<number>} - Promise that resolves with the updated balance
   */
  async updateCreditBalance() {
    return await this.getCreditBalance(true);
  }

  /**
   * Create a Stripe payment intent for purchasing credits
   * @param {string} packageId - The ID of the credit package
   * @returns {Promise} - Promise that resolves with payment intent details
   */
  async createPaymentIntent(packageId) {
    try {
      const response = await api.post('/create-payment-intent', { package_id: packageId });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to create payment intent');
    }
  }

  /**
   * Add credits after a successful payment
   * @param {string} paymentIntentId - The Stripe payment intent ID
   * @returns {Promise} - Promise that resolves when credits are added
   */
  async addCredits(paymentIntentId) {
    try {
      const response = await api.post('/add-credits', { payment_intent_id: paymentIntentId });
      
      // Force update credit balance
      await this.updateCreditBalance();
      
      // Clear restriction status if successful
      if (response.data && response.data.restriction_cleared) {
        this.isRestricted = false;
      }
      
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to add credits');
    }
  }

  /**
   * Get Stripe configuration
   * @returns {Promise} - Promise that resolves with Stripe configuration
   */
  async getStripeConfig() {
    try {
      const response = await api.get('/stripe-config');
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to get Stripe configuration');
    }
  }

  /**
   * Check if credit balance is low
   * @param {number} threshold - The threshold amount (default: 5000)
   * @returns {Promise<boolean>} - Promise that resolves with whether balance is low
   */
  async isCreditBalanceLow(threshold = 5000) {
    const balance = await this.getCreditBalance();
    return balance <= threshold && balance > 0;
  }

  /**
   * Check if credits are exhausted
   * @returns {Promise<boolean>} - Promise that resolves with whether credits are exhausted
   */
  async areCreditsExhausted() {
    const balance = await this.getCreditBalance();
    return balance <= 0;
  }

  /**
   * Calculate word cost based on length and language
   * @param {string} text - The text to calculate cost for
   * @returns {number} - Estimated credit cost
   */
  calculateWordCost(text) {
    if (!text) return 0;
    
    // Count words
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    
    // Base cost is 4 credits per word
    let baseCost = wordCount * 4;
    
    return Math.max(1, Math.ceil(baseCost));
  }

  /**
   * Add a credit balance change listener
   * @param {Function} listener - Function to call when credit balance changes
   * @returns {Function} - Function to remove the listener
   */
  addBalanceChangeListener(listener) {
    this.listeners.push(listener);
    
    // Return function to remove listener
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify listeners of credit balance changes
   * @private
   */
  _notifyListeners() {
    const balance = this.creditBalance;
    const restricted = this.isRestricted;
    
    this.listeners.forEach(listener => {
      try {
        listener({ balance, restricted });
      } catch (err) {
        console.error("Error in credit balance change listener:", err);
      }
    });
  }
}

export default new CreditService();